import { useState } from "react";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import { Upload, CheckCircle2, AlertTriangle } from "lucide-react";

const CRM_FIELDS = [
  "invoice_date", "order_date", "invoice_number", "sales_order_number",
  "customer_account_number", "customer_name", "store_location", "state", "town",
  "assigned_sales_agent", "product_sku", "product_name", "product_category",
  "quantity", "sales_ex_gst", "sales_inc_gst", "credit_value", "net_sales_value",
];

const LABELS = {
  invoice_date: "Invoice Date", order_date: "Order Date", invoice_number: "Invoice Number",
  sales_order_number: "Sales Order #", customer_account_number: "Customer Account #",
  customer_name: "Customer Name", store_location: "Store Location", state: "State", town: "Town",
  assigned_sales_agent: "Assigned Sales Agent", product_sku: "Product SKU", product_name: "Product Name",
  product_category: "Product Category", quantity: "Quantity", sales_ex_gst: "Sales Ex-GST",
  sales_inc_gst: "Sales Inc-GST", credit_value: "Credit Value", net_sales_value: "Net Sales Value",
};

function autoMap(cols) {
  const m = {};
  const norm = (s) => s.toLowerCase().replace(/[^a-z]/g, "");
  cols.forEach(col => {
    const n = norm(col);
    CRM_FIELDS.forEach(f => {
      const key = norm(f);
      if (n === key || n.includes(key) || key.includes(n)) {
        if (!m[f]) m[f] = col;
      }
    });
  });
  return m;
}

export default function SalesImport() {
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const doUpload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/sales/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setMapping(autoMap(data.columns));
      setStep("map");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };
  const doImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sales/import", { staging_id: preview.staging_id, mapping });
      setResult(data);
      setStep("done");
      toast.success(`${data.imported} rows imported`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };
  const downloadJson = (obj, name) => {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">Sales Import</h1>
        <p className="text-slate-600 mt-1">Upload Excel or CSV of sales transactions</p>
      </div>

      <div className="flex gap-2 text-sm">
        {["upload", "map", "done"].map((s, i) => (
          <div key={s} className={`px-3 py-1.5 rounded-full font-semibold ${step === s ? "bg-[#001F54] text-white" : "bg-slate-100 text-slate-500"}`}>
            {i + 1}. {s === "upload" ? "Upload" : s === "map" ? "Map Columns" : "Result"}
          </div>
        ))}
      </div>

      {step === "upload" && (
        <div className="bg-white rounded-xl border border-slate-200 p-8">
          <label className="block border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#003DA5] transition">
            <Upload className="w-10 h-10 mx-auto text-slate-400" />
            <div className="mt-3 font-semibold">Choose an Excel (.xlsx) or CSV file</div>
            <div className="text-xs text-slate-500">{file?.name || "No file selected"}</div>
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="import-file-input" />
          </label>
          <button onClick={doUpload} disabled={!file || busy} className="mt-4 mlily-btn h-11 px-5 rounded-lg font-semibold" data-testid="import-preview-btn">
            {busy ? "Uploading…" : "Preview File"}
          </button>
        </div>
      )}

      {step === "map" && preview && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
          <div className="text-sm text-slate-600">File: <span className="font-semibold">{preview.staging_id}</span> — {preview.row_count} rows detected.</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {CRM_FIELDS.map(f => (
              <div key={f} className="flex items-center gap-2">
                <div className="text-xs w-40 font-semibold text-slate-600">{LABELS[f]}</div>
                <select value={mapping[f] || ""} onChange={(e) => setMapping({ ...mapping, [f]: e.target.value })} className="flex-1 h-10 px-2 border rounded-lg text-sm" data-testid={`map-${f}`}>
                  <option value="">— skip —</option>
                  {preview.columns.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="pt-4 border-t flex gap-2 justify-end">
            <button onClick={() => setStep("upload")} className="h-11 px-5 border rounded-lg">Back</button>
            <button onClick={doImport} disabled={busy} className="mlily-btn h-11 px-5 rounded-lg font-semibold" data-testid="run-import-btn">
              {busy ? "Importing…" : "Run Import"}
            </button>
          </div>
        </div>
      )}

      {step === "done" && result && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
          <div className="flex items-center gap-3 text-emerald-700"><CheckCircle2 className="w-6 h-6" /><div className="text-lg font-semibold">Import complete</div></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric label="Rows Uploaded" value={result.rows_uploaded} />
            <Metric label="Imported" value={result.imported} />
            <Metric label="Duplicates" value={result.duplicates} tone="warn" />
            <Metric label="Unmatched Customers" value={result.unmatched.length} tone="warn" />
            <Metric label="Invalid Rows" value={result.invalid.length} tone="danger" />
            <Metric label="Total Value" value={money(result.total_value)} />
          </div>
          <div className="flex gap-2 flex-wrap">
            {result.unmatched.length > 0 && <button onClick={() => downloadJson(result.unmatched, "unmatched.json")} className="h-10 px-4 border rounded-lg text-sm">Download unmatched ({result.unmatched.length})</button>}
            {result.invalid.length > 0 && <button onClick={() => downloadJson(result.invalid, "invalid.json")} className="h-10 px-4 border rounded-lg text-sm">Download invalid ({result.invalid.length})</button>}
            <button onClick={() => { setStep("upload"); setFile(null); setResult(null); setPreview(null); }} className="mlily-btn h-10 px-4 rounded-lg text-sm font-semibold">Import another file</button>
          </div>
          {(result.unmatched.length > 0 || result.invalid.length > 0) && (
            <div className="flex items-center gap-2 text-xs text-amber-700"><AlertTriangle className="w-4 h-4" />Some rows need attention. Downloaded files list unmatched account numbers and invalid rows.</div>
          )}
        </div>
      )}
    </div>
  );
}

const Metric = ({ label, value, tone = "" }) => (
  <div className={`rounded-lg p-4 ${tone === "warn" ? "bg-amber-50" : tone === "danger" ? "bg-red-50" : "bg-slate-50"}`}>
    <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">{label}</div>
    <div className="text-2xl font-heading font-bold mt-1">{value}</div>
  </div>
);
